import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { governanceAPI, corporateAPI, meetingAPI } from '../api';
import { format, parseISO, isPast } from 'date-fns';
import { toast } from 'sonner';
import {
  FileText, Search, Clock, CheckCircle, Send, Loader2, Plus, Eye,
  ThumbsUp, ThumbsDown, Megaphone, BookOpen, Shield, FolderOpen,
  AlertCircle, Inbox, ExternalLink, ChevronRight, Vote, MessageSquare
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const StakeholdersPortal = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('documents');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Data
  const [sharedItems, setSharedItems] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [polls, setPolls] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [sharedSummaries, setSharedSummaries] = useState([]);
  const [expandedSummary, setExpandedSummary] = useState(null);

  // Request form
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestForm, setRequestForm] = useState({ request_type: 'general', subject: '', details: '', priority: 'normal' });
  const [submitting, setSubmitting] = useState(false);

  // Poll voting
  const [votingPoll, setVotingPoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [castingVote, setCastingVote] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sharedRes, annRes, docsRes, pollsRes, reqRes, summRes] = await Promise.all([
        governanceAPI.getPortalSharedItems().catch(() => ({ data: [] })),
        corporateAPI.getAllAnnouncements().catch(() => corporateAPI.getAnnouncements()).catch(() => ({ data: [] })),
        governanceAPI.getDocuments().catch(() => ({ data: [] })),
        governanceAPI.getPolls().catch(() => ({ data: [] })),
        governanceAPI.getRequests().catch(() => ({ data: [] })),
        meetingAPI.getSharedSummaries().catch(() => ({ data: [] })),
      ]);
      setSharedItems(sharedRes.data || []);
      setAnnouncements((annRes.data || []).filter(a => !a.end_date || !isPast(parseISO(a.end_date))));
      const allDocs = docsRes.data || [];
      setDocuments(allDocs.filter(d => d.status !== 'draft'));
      setPolls(pollsRes.data || []);
      setMyRequests((reqRes.data || []).filter(r => r.submitted_by === user?.id));
      setSharedSummaries(summRes.data || []);
    } catch (err) { toast.error('Failed to load portal data'); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmitRequest = async () => {
    if (!requestForm.subject.trim()) { toast.error('Subject is required'); return; }
    setSubmitting(true);
    try {
      await governanceAPI.createRequest(requestForm);
      toast.success('Request submitted successfully');
      setRequestDialog(false);
      setRequestForm({ request_type: 'general', subject: '', details: '', priority: 'normal' });
      fetchData();
    } catch (err) { toast.error('Failed to submit request'); }
    finally { setSubmitting(false); }
  };

  const handleCastVote = async () => {
    if (!selectedOption || !votingPoll) return;
    setCastingVote(true);
    try {
      await governanceAPI.votePoll(votingPoll.id, { option: selectedOption });
      toast.success('Vote cast successfully');
      setVotingPoll(null);
      setSelectedOption('');
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to cast vote'); }
    finally { setCastingVote(false); }
  };

  const term = searchTerm.toLowerCase();

  const filteredShared = sharedItems.filter(i =>
    (i.name || i.title || '').toLowerCase().includes(term) ||
    (i.credential_type || i.category || i.record_type || '').toLowerCase().includes(term)
  );
  const filteredAnnouncements = announcements.filter(a =>
    (a.title || '').toLowerCase().includes(term) || (a.message || '').toLowerCase().includes(term)
  );
  const filteredDocs = documents.filter(d =>
    (d.title || '').toLowerCase().includes(term) || (d.document_type || '').toLowerCase().includes(term)
  );
  const filteredPolls = polls.filter(p =>
    (p.title || '').toLowerCase().includes(term) || (p.description || '').toLowerCase().includes(term)
  );
  const filteredRequests = myRequests.filter(r =>
    (r.subject || '').toLowerCase().includes(term) || (r.details || '').toLowerCase().includes(term)
  );

  const typeIcons = { credential: Shield, resource: FolderOpen, record: BookOpen };
  const statusColors = {
    pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800', in_review: 'bg-blue-100 text-blue-800'
  };
  const statusIcons = { pending: Clock, approved: CheckCircle, rejected: AlertCircle, in_review: Eye };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="stakeholders-portal">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Stakeholders Portal</h1>
          <p className="text-sm text-[#5A6570] mt-1">Access shared documents, announcements, and submit requests</p>
        </div>
        <Button onClick={() => { setRequestForm({ request_type: 'general', subject: '', details: '', priority: 'normal' }); setRequestDialog(true); }}
          className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="submit-request-btn">
          <Plus className="h-4 w-4 mr-2" />Submit Request
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: 'Shared Documents', count: sharedItems.length, icon: FileText, tab: 'documents' },
          { label: 'Summaries', count: sharedSummaries.length, icon: MessageSquare, tab: 'summaries' },
          { label: 'Announcements', count: announcements.length, icon: Megaphone, tab: 'announcements' },
          { label: 'For Review', count: documents.length, icon: Eye, tab: 'reviews' },
          { label: 'Active Polls', count: polls.filter(p => p.status === 'active').length, icon: Vote, tab: 'polls' },
          { label: 'My Requests', count: myRequests.length, icon: Inbox, tab: 'requests' },
        ].map(s => (
          <Card key={s.tab} className={`border-[#E2E6EA] cursor-pointer transition-all hover:border-[#00A3A3]/50 ${tab === s.tab ? 'ring-2 ring-[#00A3A3]/30 border-[#00A3A3]' : ''}`}
            onClick={() => setTab(s.tab)} data-testid={`portal-stat-${s.tab}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${tab === s.tab ? 'bg-[#00A3A3]/10' : 'bg-[#F0F2F4]'}`}>
                <s.icon className={`h-5 w-5 ${tab === s.tab ? 'text-[#00A3A3]' : 'text-[#5A6570]'}`} />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1A1D21]">{s.count}</p>
                <p className="text-[11px] text-[#5A6570]">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={v => { setTab(v); setSearchTerm(''); }}>
        <TabsList className="tabs-grid-6 bg-[#F0F2F4] p-1" data-testid="portal-tabs">
          <TabsTrigger value="documents" className="data-[state=active]:bg-white text-xs" data-testid="portal-tab-documents"><FileText className="h-3.5 w-3.5 mr-1" />Documents</TabsTrigger>
          <TabsTrigger value="summaries" className="data-[state=active]:bg-white text-xs" data-testid="portal-tab-summaries"><MessageSquare className="h-3.5 w-3.5 mr-1" />Summaries</TabsTrigger>
          <TabsTrigger value="announcements" className="data-[state=active]:bg-white text-xs" data-testid="portal-tab-announcements"><Megaphone className="h-3.5 w-3.5 mr-1" />Announcements</TabsTrigger>
          <TabsTrigger value="reviews" className="data-[state=active]:bg-white text-xs" data-testid="portal-tab-reviews"><Eye className="h-3.5 w-3.5 mr-1" />Review</TabsTrigger>
          <TabsTrigger value="polls" className="data-[state=active]:bg-white text-xs" data-testid="portal-tab-polls"><Vote className="h-3.5 w-3.5 mr-1" />Polls</TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-white text-xs" data-testid="portal-tab-requests">
            <Inbox className="h-3.5 w-3.5 mr-1" />My Requests
            {myRequests.filter(r => r.status === 'pending').length > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-[10px] h-4 px-1">{myRequests.filter(r => r.status === 'pending').length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ====== SHARED DOCUMENTS ====== */}
        <TabsContent value="documents" className="mt-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
              <Input placeholder="Search shared documents..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-portal-documents" />
            </div>
            {filteredShared.length === 0 ? (
              <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
                <FileText className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches found' : 'No shared documents yet'}</h3>
                <p className="text-sm text-[#5A6570]">{searchTerm ? `No documents match "${searchTerm}"` : 'The Secretariat will share documents here when available'}</p>
              </CardContent></Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {filteredShared.map(item => {
                  const Icon = typeIcons[item._type] || FileText;
                  return (
                    <Card key={`${item._type}-${item.id}`} className="border-[#E2E6EA] hover:border-[#00A3A3]/40 transition-colors" data-testid={`shared-item-${item.id}`}>
                      <CardContent className="p-4 flex items-start gap-3">
                        <div className="p-2 bg-[#F0F2F4] rounded-lg shrink-0">
                          <Icon className="h-5 w-5 text-[#5A6570]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-[#1A1D21] truncate">{item.name || item.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">{item._type}</Badge>
                            {item.credential_type && <span className="text-[10px] text-[#A8B1B9]">{item.credential_type}</span>}
                            {item.category && <span className="text-[10px] text-[#A8B1B9]">{item.category}</span>}
                            {item.record_type && <span className="text-[10px] text-[#A8B1B9]">{item.record_type}</span>}
                          </div>
                          {(item.description || item.notes) && (
                            <p className="text-xs text-[#5A6570] mt-1.5 line-clamp-2">{item.description || item.notes}</p>
                          )}
                          {item.expiry_date && (
                            <p className="text-[10px] text-amber-600 mt-1">
                              <Clock className="h-3 w-3 inline mr-0.5" />
                              Expires: {format(parseISO(item.expiry_date), 'MMM d, yyyy')}
                            </p>
                          )}
                        </div>
                        {item.file_url && (
                          <a href={item.file_url} target="_blank" rel="noreferrer" className="shrink-0">
                            <Button size="sm" variant="outline" className="h-7 text-xs">
                              <ExternalLink className="h-3 w-3 mr-1" />View
                            </Button>
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>


        {/* ====== SUMMARIES ====== */}
        <TabsContent value="summaries" className="mt-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
              <Input placeholder="Search meeting summaries..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-portal-summaries" />
            </div>
            {sharedSummaries.filter(s => (s.title || '').toLowerCase().includes(term)).length === 0 ? (
              <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches found' : 'No summaries shared yet'}</h3>
                <p className="text-sm text-[#5A6570]">{searchTerm ? `No summaries match "${searchTerm}"` : 'Meeting summaries will appear here once shared by the Secretariat'}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {sharedSummaries
                  .filter(s => (s.title || '').toLowerCase().includes(term))
                  .map(meeting => (
                  <Card key={meeting.id} className="border-[#E2E6EA]" data-testid={`portal-summary-${meeting.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpandedSummary(expandedSummary === meeting.id ? null : meeting.id)}>
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-[#00A3A3]/10 rounded-lg shrink-0">
                            <MessageSquare className="h-5 w-5 text-[#00A3A3]" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-[#1A1D21]">{meeting.title}</h4>
                            <div className="flex items-center gap-3 mt-1 text-[10px] text-[#A8B1B9]">
                              {meeting.date_time && <span>Meeting: {format(parseISO(meeting.date_time), 'MMM d, yyyy')}</span>}
                              {meeting.summary_generated_at && <span>Summary generated: {format(parseISO(meeting.summary_generated_at), 'MMM d, yyyy h:mm a')}</span>}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className={`h-5 w-5 text-[#A8B1B9] shrink-0 transition-transform ${expandedSummary === meeting.id ? 'rotate-90' : ''}`} />
                      </div>
                      {expandedSummary === meeting.id && meeting.summary_html && (
                        <div className="mt-4 pt-4 border-t border-[#E2E6EA]">
                          <div className="prose prose-sm max-w-none text-[#1A1D21]" dangerouslySetInnerHTML={{ __html: meeting.summary_html }} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ====== ANNOUNCEMENTS ====== */}
        <TabsContent value="announcements" className="mt-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
              <Input placeholder="Search announcements..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-portal-announcements" />
            </div>
            {filteredAnnouncements.length === 0 ? (
              <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
                <Megaphone className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches found' : 'No announcements'}</h3>
                <p className="text-sm text-[#5A6570]">{searchTerm ? `No announcements match "${searchTerm}"` : 'No active announcements at this time'}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredAnnouncements.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(ann => (
                  <Card key={ann.id} className="border-[#E2E6EA]" data-testid={`portal-announcement-${ann.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-amber-50 rounded-lg shrink-0">
                          <Megaphone className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-[#1A1D21]">{ann.title}</h4>
                          <p className="text-xs text-[#5A6570] mt-1 whitespace-pre-wrap">{ann.message}</p>
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-[#A8B1B9]">
                            {ann.created_at && <span>{format(parseISO(ann.created_at), 'MMM d, yyyy')}</span>}
                            {ann.end_date && <span>Expires: {format(parseISO(ann.end_date), 'MMM d, yyyy')}</span>}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ====== DOCUMENT REVIEW ====== */}
        <TabsContent value="reviews" className="mt-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
              <Input placeholder="Search documents for review..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-portal-reviews" />
            </div>
            {filteredDocs.length === 0 ? (
              <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
                <Eye className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches found' : 'No documents for review'}</h3>
                <p className="text-sm text-[#5A6570]">{searchTerm ? `No documents match "${searchTerm}"` : 'Documents requiring your review will appear here'}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredDocs.map(doc => {
                  const docStatusColors = {
                    pending: 'bg-amber-100 text-amber-800', under_review: 'bg-blue-100 text-blue-800',
                    approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800'
                  };
                  return (
                    <Card key={doc.id} className="border-[#E2E6EA]" data-testid={`portal-doc-${doc.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-[#F0F2F4] rounded-lg shrink-0">
                              <FileText className="h-5 w-5 text-[#5A6570]" />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-[#1A1D21]">{doc.title}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-[10px]">{doc.document_type || 'Document'}</Badge>
                                <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded ${docStatusColors[doc.status] || 'bg-gray-100 text-gray-800'}`}>
                                  {doc.status?.replace('_', ' ')}
                                </span>
                              </div>
                              {doc.description && <p className="text-xs text-[#5A6570] mt-1.5 line-clamp-2">{doc.description}</p>}
                              <div className="flex items-center gap-3 mt-2 text-[10px] text-[#A8B1B9]">
                                {doc.created_at && <span>Uploaded: {format(parseISO(doc.created_at), 'MMM d, yyyy')}</span>}
                                {doc.deadline && <span className="text-amber-600">Due: {format(parseISO(doc.deadline), 'MMM d, yyyy')}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            {doc.file_url && (
                              <a href={doc.file_url} target="_blank" rel="noreferrer">
                                <Button size="sm" variant="outline" className="h-7 text-xs"><ExternalLink className="h-3 w-3 mr-1" />View</Button>
                              </a>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ====== POLLS ====== */}
        <TabsContent value="polls" className="mt-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
              <Input placeholder="Search polls..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-portal-polls" />
            </div>
            {filteredPolls.length === 0 ? (
              <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
                <Vote className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches found' : 'No polls available'}</h3>
                <p className="text-sm text-[#5A6570]">{searchTerm ? `No polls match "${searchTerm}"` : 'Active polls will appear here for your participation'}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredPolls.map(poll => {
                  const isActive = poll.status === 'active';
                  const hasVoted = poll.votes?.some(v => v.user_id === user?.id);
                  const totalVotes = poll.votes?.length || 0;
                  return (
                    <Card key={poll.id} className={`border-[#E2E6EA] ${isActive && !hasVoted ? 'border-l-4 border-l-[#00A3A3]' : ''}`} data-testid={`portal-poll-${poll.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-semibold text-[#1A1D21]">{poll.title}</h4>
                              <Badge className={isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'} variant="outline">
                                {poll.status}
                              </Badge>
                              {hasVoted && <Badge className="bg-[#00A3A3]/10 text-[#00A3A3] text-[10px]">Voted</Badge>}
                            </div>
                            {poll.description && <p className="text-xs text-[#5A6570] mt-0.5">{poll.description}</p>}
                            {/* Show results if voted or closed */}
                            {(hasVoted || !isActive) && poll.options && (
                              <div className="mt-3 space-y-1.5">
                                {poll.options.map((opt, i) => {
                                  const optVotes = poll.votes?.filter(v => v.option === opt).length || 0;
                                  const pct = totalVotes ? Math.round((optVotes / totalVotes) * 100) : 0;
                                  const myVote = poll.votes?.find(v => v.user_id === user?.id);
                                  return (
                                    <div key={i} className="relative">
                                      <div className="flex items-center justify-between text-xs mb-0.5">
                                        <span className={`font-medium ${myVote?.option === opt ? 'text-[#00A3A3]' : 'text-[#1A1D21]'}`}>
                                          {opt} {myVote?.option === opt && <CheckCircle className="h-3 w-3 inline ml-0.5" />}
                                        </span>
                                        <span className="text-[#A8B1B9]">{pct}% ({optVotes})</span>
                                      </div>
                                      <div className="h-2 bg-[#F0F2F4] rounded-full overflow-hidden">
                                        <div className="h-full bg-[#00A3A3] rounded-full transition-all" style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                  );
                                })}
                                <p className="text-[10px] text-[#A8B1B9] pt-1">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</p>
                              </div>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-[#A8B1B9]">
                              {poll.created_at && <span>Created: {format(parseISO(poll.created_at), 'MMM d, yyyy')}</span>}
                              {poll.end_date && <span>Ends: {format(parseISO(poll.end_date), 'MMM d, yyyy')}</span>}
                            </div>
                          </div>
                          {isActive && !hasVoted && (
                            <Button size="sm" className="bg-[#00A3A3] hover:bg-[#008585] shrink-0 h-8 text-xs"
                              onClick={() => { setVotingPoll(poll); setSelectedOption(''); }} data-testid={`vote-poll-${poll.id}`}>
                              <ThumbsUp className="h-3 w-3 mr-1" />Vote
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ====== MY REQUESTS ====== */}
        <TabsContent value="requests" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
                <Input placeholder="Search your requests..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-portal-requests" />
              </div>
              <Button onClick={() => { setRequestForm({ request_type: 'general', subject: '', details: '', priority: 'normal' }); setRequestDialog(true); }}
                className="bg-[#00A3A3] hover:bg-[#008585] shrink-0" data-testid="new-request-btn">
                <Plus className="h-4 w-4 mr-2" />New Request
              </Button>
            </div>
            {filteredRequests.length === 0 ? (
              <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
                <Inbox className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <h3 className="text-base font-medium text-[#1A1D21] mb-1">{searchTerm ? 'No matches found' : 'No requests submitted'}</h3>
                <p className="text-sm text-[#5A6570]">{searchTerm ? `No requests match "${searchTerm}"` : 'Submit a request to the Secretariat using the button above'}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredRequests.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(req => {
                  const StatusIcon = statusIcons[req.status] || Clock;
                  const typeLabels = { general: 'General', document_access: 'Document Access', meeting_request: 'Meeting Request', extension: 'Extension', other: 'Other' };
                  return (
                    <Card key={req.id} className="border-[#E2E6EA]" data-testid={`my-request-${req.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg shrink-0 ${
                            req.status === 'approved' ? 'bg-green-50' : req.status === 'rejected' ? 'bg-red-50' : 'bg-amber-50'
                          }`}>
                            <StatusIcon className={`h-5 w-5 ${
                              req.status === 'approved' ? 'text-green-600' : req.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm font-semibold text-[#1A1D21]">{req.subject}</h4>
                              <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded ${statusColors[req.status] || ''}`}>
                                {req.status?.replace('_', ' ')}
                              </span>
                              <Badge variant="outline" className="text-[10px]">{typeLabels[req.request_type] || req.request_type}</Badge>
                            </div>
                            {req.details && <p className="text-xs text-[#5A6570] mt-1">{req.details}</p>}
                            {req.admin_notes && (
                              <div className="mt-2 p-2 bg-[#F8F9FA] rounded text-xs text-[#5A6570]">
                                <span className="font-medium">Secretariat response:</span> {req.admin_notes}
                              </div>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-[#A8B1B9]">
                              <span>Submitted: {req.created_at ? format(parseISO(req.created_at), 'MMM d, yyyy h:mm a') : ''}</span>
                              {req.reviewed_at && <span>Reviewed: {format(parseISO(req.reviewed_at), 'MMM d, yyyy')}</span>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Submit Request Dialog */}
      <Dialog open={requestDialog} onOpenChange={setRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[#00A3A3]" />Submit a Request</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Request Type</Label>
              <Select value={requestForm.request_type} onValueChange={v => setRequestForm(f => ({ ...f, request_type: v }))}>
                <SelectTrigger className="border-[#E2E6EA] mt-1" data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Inquiry</SelectItem>
                  <SelectItem value="document_access">Document Access</SelectItem>
                  <SelectItem value="meeting_request">Meeting Request</SelectItem>
                  <SelectItem value="extension">Deadline Extension</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject <span className="text-red-500">*</span></Label>
              <Input value={requestForm.subject} onChange={e => setRequestForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief subject of your request" className="mt-1 border-[#E2E6EA]" data-testid="request-subject-input" />
            </div>
            <div>
              <Label>Details</Label>
              <Textarea value={requestForm.details} onChange={e => setRequestForm(f => ({ ...f, details: e.target.value }))} placeholder="Provide additional details about your request..." rows={4} className="mt-1 border-[#E2E6EA]" data-testid="request-details-input" />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={requestForm.priority} onValueChange={v => setRequestForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="border-[#E2E6EA] mt-1" data-testid="request-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitRequest} disabled={submitting || !requestForm.subject.trim()} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-submit-request-btn">
              {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vote on Poll Dialog */}
      <Dialog open={!!votingPoll} onOpenChange={v => { if (!v) { setVotingPoll(null); setSelectedOption(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cast Your Vote</DialogTitle></DialogHeader>
          {votingPoll && (
            <div className="space-y-4 py-2">
              <div>
                <h4 className="text-sm font-semibold text-[#1A1D21]">{votingPoll.title}</h4>
                {votingPoll.description && <p className="text-xs text-[#5A6570] mt-1">{votingPoll.description}</p>}
              </div>
              <RadioGroup value={selectedOption} onValueChange={setSelectedOption}>
                {votingPoll.options?.map((opt, i) => (
                  <div key={i} className="flex items-center space-x-3 p-3 rounded-lg border border-[#E2E6EA] hover:border-[#00A3A3]/40 transition-colors cursor-pointer" onClick={() => setSelectedOption(opt)}>
                    <RadioGroupItem value={opt} id={`opt-${i}`} />
                    <Label htmlFor={`opt-${i}`} className="cursor-pointer flex-1 text-sm">{opt}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVotingPoll(null); setSelectedOption(''); }}>Cancel</Button>
            <Button onClick={handleCastVote} disabled={!selectedOption || castingVote} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-vote-btn">
              {castingVote && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Cast Vote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StakeholdersPortal;
